class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-recommendations')?.load();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    if (parsedState.product_id) CartDrawerRecommendations.lastAddedProductId = parsedState.product_id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        // Already open (product added from inside the drawer): the focused element was just replaced
        trapFocus(document.getElementById('CartDrawer'), this.querySelector('.drawer__inner'));
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerRecommendations extends HTMLElement {
  static storageKey = 'cart-drawer-last-added';
  static requests = new Map();

  static get lastAddedProductId() {
    try {
      return sessionStorage.getItem(this.storageKey);
    } catch (e) {
      return null;
    }
  }

  static set lastAddedProductId(productId) {
    try {
      sessionStorage.setItem(this.storageKey, productId);
    } catch (e) {
      // Storage unavailable: recommendations fall back to the first cart line
    }
  }

  constructor() {
    super();
    this.addEventListener('click', this.onAddClick.bind(this));
  }

  connectedCallback() {
    // The drawer contents are re-rendered on every cart change, only fetch once the drawer is visible
    if (this.closest('cart-drawer')?.classList.contains('active')) this.load();
  }

  get cartProductIds() {
    return this.dataset.cartProductIds.split(',');
  }

  // Last product added to the cart, as long as it is still in it (first cart line otherwise)
  get sourceProduct() {
    const index = Math.max(this.cartProductIds.indexOf(CartDrawerRecommendations.lastAddedProductId), 0);
    return {
      id: this.cartProductIds[index],
      vendor: JSON.parse(this.dataset.cartVendors)[index],
    };
  }

  static normalizeVendor(vendor) {
    return (vendor || '').trim().toLowerCase();
  }

  // Resolves with the product list markup rendered at the given url, cached per url
  static fetchList(url) {
    const requests = CartDrawerRecommendations.requests;
    if (!requests.has(url)) {
      requests.set(
        url,
        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const list = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.cart-drawer-recommendations__list');
            return list ? list.innerHTML : '';
          })
          .catch((e) => {
            requests.delete(url);
            console.error(e);
            return '';
          })
      );
    }
    return requests.get(url);
  }

  load() {
    const { id, vendor } = this.sourceProduct;
    if (!id || this.loadedProductId === id) return;
    this.loadedProductId = id;

    Promise.all([
      CartDrawerRecommendations.fetchList(`${this.dataset.url}&product_id=${id}`),
      vendor ? CartDrawerRecommendations.fetchList(`${this.dataset.vendorUrl}&q=${encodeURIComponent(vendor)}`) : '',
    ]).then(([related, sameVendor]) => this.render(related + sameVendor, vendor));
  }

  // Products of the same brand come first: related ones, then the brand's best sellers.
  // Other related products are only shown when the brand has nothing else in stock.
  render(html, vendor) {
    const list = document.createElement('ul');
    list.className = 'cart-drawer-recommendations__list list-unstyled';
    list.setAttribute('role', 'list');
    list.innerHTML = html;

    const seen = new Set(this.cartProductIds);
    const items = [...list.children].filter((item) => {
      if (seen.has(item.dataset.productId)) return false;
      seen.add(item.dataset.productId);
      return true;
    });
    const sourceVendor = CartDrawerRecommendations.normalizeVendor(vendor);
    const sameVendorItems = items.filter(
      (item) => sourceVendor && CartDrawerRecommendations.normalizeVendor(item.dataset.vendor) === sourceVendor
    );
    const selected = (sameVendorItems.length ? sameVendorItems : items).slice(0, parseInt(this.dataset.limit));

    list.replaceChildren(...selected);
    this.querySelector('.cart-drawer-recommendations__content').replaceChildren(list);
    this.hidden = selected.length === 0;
  }

  onAddClick(event) {
    const button = event.target.closest('.cart-drawer-recommendations__add');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const cartDrawer = this.closest('cart-drawer');
    const errorMessage = this.querySelector('.cart-drawer-recommendations__error');
    errorMessage.hidden = true;

    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');
    button.querySelector('.loading__spinner').classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', button.dataset.variantId);
    formData.append('quantity', 1);
    formData.append(
      'sections',
      cartDrawer.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          errorMessage.textContent = response.description;
          errorMessage.hidden = false;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendations',
          productVariantId: button.dataset.variantId,
          cartData: response,
        });
        cartDrawer.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
        errorMessage.textContent = window.cartStrings.error;
        errorMessage.hidden = false;
      })
      .finally(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        button.querySelector('.loading__spinner').classList.add('hidden');
      });
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
